import React, { useEffect, useState } from 'react';
import '../styles/GameTest.css';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCampaignById } from '../services/campaignService';
import { Campaign } from '../types/campaign';
import { collection, query, where, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

const GameTest: React.FC = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const { currentUser, userProfile } = useAuth();
  const [userName, setUserName] = useState<string>('');
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [gameStatus, setGameStatus] = useState<'playing' | 'win' | 'lose'>('playing');
  const [iframeUrl, setIframeUrl] = useState<string>('');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [checkingResults, setCheckingResults] = useState(false);
  const [resultTimeout, setResultTimeout] = useState(false);
  const [componentMountTime] = useState<number>(Date.now());

  useEffect(() => {
    // Redirect unauthenticated users
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Set user name from Firebase
    if (currentUser.displayName) {
      const firstName = currentUser.displayName.split(' ')[0];
      setUserName(firstName);
    }
  }, [currentUser, navigate]);

  // Loading timeout - show "Try Again" button after 7 seconds
  useEffect(() => {
    if (!loading) return;

    console.log('⏰ Starting loading timeout timer (7 seconds)');
    
    const timeoutTimer = setTimeout(() => {
      if (loading) {
        console.warn('⏰ Loading timeout - showing retry button');
        setLoadingTimeout(true);
      }
    }, 7000); // 7 seconds
    
    return () => {
      console.log('⏰ Clearing loading timeout timer');
      clearTimeout(timeoutTimer);
    };
  }, [loading]);

  useEffect(() => {
    const fetchCampaign = async () => {
      if (!campaignId) {
        setLoading(false);
        return;
      }
      
      // Wait for userProfile to load before building iframe URL
      // This prevents race condition where URL is built without parameters
      if (currentUser && !userProfile) {
        console.log('⏳ Waiting for user profile to load before building iframe URL...');
        return; // Exit early, will retry when userProfile loads
      }
      
      const campaignData = await getCampaignById(campaignId);
      if (campaignData) {
        setCampaign(campaignData);
        
        // Build iframe URL with user data, campaign ID, and UID
        let url = campaignData.iframeUrl;
        if (currentUser?.email && userProfile?.fornavn && currentUser?.uid) {
          const separator = url.includes('?') ? '&' : '?';
          url = `${url}${separator}e-mail=${encodeURIComponent(currentUser.email)}&name=${encodeURIComponent(userProfile.fornavn)}&campaignId=${encodeURIComponent(campaignId)}&uid=${encodeURIComponent(currentUser.uid)}`;
          console.log('✅ Iframe URL built with parameters:', url);
          console.log('🔑 UID included in URL:', currentUser.uid);
        } else {
          console.warn('⚠️ Building iframe URL without parameters (user data not available)');
        }
        setIframeUrl(url);
      }
      setLoading(false);
    };

    fetchCampaign();
  }, [campaignId, currentUser, userProfile]);

  useEffect(() => {
    // Load the responsive iframe script
    if (campaign?.iframeScript) {
      const script = document.createElement('script');
      script.src = campaign.iframeScript;
      script.async = true;
      document.body.appendChild(script);

      return () => {
        // Clean up script when component unmounts
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [campaign]);

  // Firestore real-time listener for campaign completion
  // Listens to ALL user completions (not tied to specific campaign URL)
  useEffect(() => {
    if (!currentUser) {
      console.log('⚠️ No user logged in, skipping completion listener');
      return;
    }

    console.log('🎮 Setting up Firestore completion listener for user:', currentUser.uid);
    
    // Query ALL completions for this user, ordered by most recent
    const completionsQuery = query(
      collection(db, 'completed_campaigns'),
      where('userId', '==', currentUser.uid),
      orderBy('lastCompletedAt', 'desc')
    );
    
    // Track the most recent completion timestamp to detect NEW completions
    let lastCompletionTimestamp: Timestamp | null = null;
    
    // Real-time listener
    const unsubscribe = onSnapshot(completionsQuery, (querySnapshot) => {
      console.log('📡 Completions query updated. Total documents:', querySnapshot.size);
      
      if (querySnapshot.empty) {
        console.log('📭 No completion documents exist yet for this user');
        return;
      }
      
      // Get the most recent completion (first document due to orderBy desc)
      const mostRecentDoc = querySnapshot.docs[0];
      const data = mostRecentDoc.data();
      const currentTimestamp = data.lastCompletedAt;
      
      console.log('📡 Most recent completion:', {
        campaignId: data.campaignId,
        count: data.completionCount,
        lastStatus: data.lastStatus,
        lastCompletedAt: currentTimestamp,
        componentMountTime: componentMountTime
      });
      
      // Check if this completion happened AFTER the component mounted
      // This ensures we only show modals for NEW completions, not old ones
      const completionTimeMillis = currentTimestamp?.toMillis() || 0;
      const isNewCompletion = completionTimeMillis > componentMountTime;
      
      console.log('🔍 Completion time check:', {
        completionTime: completionTimeMillis,
        mountTime: componentMountTime,
        isNew: isNewCompletion,
        timeDiff: completionTimeMillis - componentMountTime
      });
      
      // If this is first load, just store the timestamp
      if (lastCompletionTimestamp === null) {
        lastCompletionTimestamp = currentTimestamp;
        console.log('📊 Initial completion timestamp stored');
        
        // Check if this completion is NEW (happened after page load)
        if (isNewCompletion) {
          console.log('🎉 NEW completion detected on first load!');
          setCheckingResults(false);
          
          // Wait 2 seconds before showing modal (let game animations finish)
          setTimeout(() => {
            if (data.lastStatus === 'win') {
              console.log('🏆 Showing WIN modal');
              setGameStatus('win');
            } else if (data.lastStatus === 'lose') {
              console.log('😢 Showing LOSE modal');
              setGameStatus('lose');
            }
          }, 0);
        } else {
          console.log('📊 Old completion (before page load), not showing modal');
        }
        return;
      }
      
      // If timestamp is newer than previous, user just completed another game
      if (currentTimestamp && currentTimestamp > lastCompletionTimestamp) {
        console.log('🎉 NEW completion detected! Timestamp changed');
        setCheckingResults(false); // Clear checking state if it was showing
        
        // Wait 2 seconds before showing modal (let game animations finish)
        setTimeout(() => {
          if (data.lastStatus === 'win') {
            console.log('🏆 Showing WIN modal');
            setGameStatus('win');
          } else if (data.lastStatus === 'lose') {
            console.log('😢 Showing LOSE modal');
            setGameStatus('lose');
          }
        }, 0);
        
        // Update the timestamp for next completion
        lastCompletionTimestamp = currentTimestamp;
      }
    }, (error) => {
      console.error('❌ Error in completion listener:', error);
      setConnectionError('Unable to check game results. Please refresh the page.');
      setCheckingResults(false);
    });
    
    return () => {
      console.log('🎮 Removing Firestore completion listener');
      unsubscribe();
    };
  }, [currentUser, componentMountTime]);

  // Result timeout timer - warn if no results after 5 minutes
  useEffect(() => {
    if (gameStatus !== 'playing') return;

    console.log('⏰ Starting result timeout timer (5 minutes)');
    
    const timeoutTimer = setTimeout(() => {
      if (gameStatus === 'playing') {
        console.warn('⏰ Result timeout - no completion detected after 5 minutes');
        setResultTimeout(true);
        setCheckingResults(false);
      }
    }, 300000); // 5 minutes
    
    return () => {
      console.log('⏰ Clearing result timeout timer');
      clearTimeout(timeoutTimer);
    };
  }, [gameStatus]);

  // Notify parent iframe when game status changes (modal appears/disappears)
  useEffect(() => {
    // Trigger height recalculation when win/lose modals appear
    if (gameStatus === 'win' || gameStatus === 'lose') {
      window.dispatchEvent(new CustomEvent('wiin-height-change'));
      console.log('📏 Notified parent: Modal appeared');
    }
  }, [gameStatus]);

  // Notify parent when checking results overlay appears/disappears
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('wiin-height-change'));
  }, [checkingResults, connectionError, resultTimeout]);

  // Notify parent when iframe loads
  useEffect(() => {
    if (iframeUrl) {
      // Give iframe time to load, then notify parent
      const timer = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('wiin-height-change'));
        console.log('📏 Notified parent: Game iframe loaded');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [iframeUrl]);

  const handleProfileClick = () => {
    navigate('/profil');
  };

  const handleBackToMain = () => {
    navigate('/main');
  };

  const handleGoToWallet = () => {
    navigate('/wallet');
  };

  const handleLogoClick = () => {
    if (currentUser) {
      navigate('/main');
    }
  };

  if (loading) {
    return (
      <div className="game-test-container">
        <div className="game-loading">
          <div className="spinner"></div>
          <p>Indlæser spil...</p>
          {loadingTimeout && (
            <div className="loading-timeout-actions">
              <p className="timeout-message">Indlæsning tager længere tid end forventet</p>
              <button 
                className="game-modal-button" 
                onClick={() => window.location.reload()}
              >
                Prøv igen
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="game-test-container">
        <div className="game-error">
          <h2>Kampagne ikke fundet</h2>
          <button onClick={handleBackToMain} className="game-back-button">Tilbage til hovedside</button>
        </div>
      </div>
    );
  }

  return (
    <div className="game-test-container fade-in">
      {/* Wiin Account Header */}
      <div className="wiin-account-header">
        <div className="wiin-logo-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <img src="/images/logo.png" alt="Wiin Logo" className="wiin-logo" />
        </div>
        <div className="wiin-profile-info">
          <div className="wiin-greeting-container">
            <h2 className="wiin-greeting">Hej {userName || 'Hallo'}!</h2>
            <img src="/images/User_icon.png" alt="User Avatar" className="wiin-avatar" />
          </div>
          <p className="wiin-profile-link" onClick={handleProfileClick}>Din profil <span className="wiin-arrow">➔</span></p>
        </div>
      </div>

      {/* Game Container */}
      <div className="game-container">
        {iframeUrl && (
          <iframe 
            title={campaign.title} 
            src={iframeUrl} 
            scrolling="no" 
            allow="autoplay; fullscreen"
          />
        )}
      </div>

      {/* Win Modal */}
      {gameStatus === 'win' && (
        <div className="game-modal-backdrop">
          <div className="game-modal">
            <h2 className="game-modal-title">Tillykke</h2>
            <p className="game-modal-message">
              Du har vundet en præmie! Se din præmie i din Wallet
            </p>
            <button className="game-modal-button" onClick={handleGoToWallet}>
              Se Wallet
            </button>
          </div>
        </div>
      )}

      {/* Lose Modal */}
      {gameStatus === 'lose' && (
        <div className="game-modal-backdrop">
          <div className="game-modal">
            <h2 className="game-modal-title">Ingen held</h2>
            <p className="game-modal-message">
              Forsøg igen i en af vores andre konkurrencer
            </p>
            <button className="game-modal-button" onClick={handleBackToMain}>
              Gå til spilsiden
            </button>
          </div>
        </div>
      )}

      {/* Checking Results Overlay */}
      {checkingResults && (
        <div className="game-loading-overlay">
          <div className="game-loading-content">
            <div className="spinner"></div>
            <p>Tjekker dine resultater...</p>
          </div>
        </div>
      )}

      {/* Connection Error Overlay */}
      {connectionError && (
        <div className="game-error-overlay">
          <div className="game-error-content">
            <h3>⚠️ Forbindelsesfejl</h3>
            <p>{connectionError}</p>
            <button className="game-modal-button" onClick={() => window.location.reload()}>
              Genindlæs side
            </button>
            <button className="game-modal-button secondary" onClick={handleBackToMain}>
              Tilbage til hovedside
            </button>
          </div>
        </div>
      )}

      {/* Result Timeout Warning */}
      {resultTimeout && (
        <div className="game-warning-overlay">
          <div className="game-warning-content">
            <h3>⏰ Tager længere end forventet</h3>
            <p>Dine resultater bliver behandlet. Du kan:</p>
            <button className="game-modal-button" onClick={() => window.location.reload()}>
              Genindlæs for at tjekke resultater
            </button>
            <button className="game-modal-button" onClick={handleGoToWallet}>
              Tjek din Wallet
            </button>
            <button className="game-modal-button secondary" onClick={() => setResultTimeout(false)}>
              Fortsæt med at vente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameTest; 